import {
  OEButton,
  OETextInput,
  height,
  useToastMessage,
} from '@openeducation/oe-it-components';
import { styled } from '@openeducation/oe-it-themes';
import React, { useState } from 'react';

import { InfoItem } from '../../atoms/info-item/info-item';
import type { OlderLiveClassStudent } from '../../interfaces/older-live-class';

export interface ClassFeedbackModalItemProps {
  feedbackSent?: boolean;
  student: OlderLiveClassStudent;
  onSendFeedback: (feedback: string) => Promise<void>;
}

export const ClassFeedbackModalItem = ({
  student,
  onSendFeedback,
  feedbackSent = false,
}: ClassFeedbackModalItemProps) => {
  const { showToast } = useToastMessage();
  const [feedback, setFeedback] = useState('');
  const [isLoading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSendFeedback(feedback);
      showToast({
        message: `Feedback sent to ${student.attendanceName}`,
        type: 'success',
        autoHide: true,
      });
    } catch {
      showToast({
        message: `Error sending feedback to ${student.attendanceName}`,
        type: 'error',
        autoHide: true,
      });
    }
    setLoading(false);
  };

  return (
    <Container>
      <TitleContainer>
        <InfoItem title="Name" text={student.attendanceName} />
        <InfoItem
          title="Status"
          text={feedbackSent ? 'Done' : 'Pending'}
          textType={feedbackSent ? 'dark' : 'warning'}
        />
      </TitleContainer>
      {!feedbackSent && (
        <>
          <OETextInput
            multiline
            disabled={isLoading}
            style={[height(100)]}
            onChangeText={setFeedback}
            value={feedback}
            placeholder="Enter feedback here..."
          />
          <FooterContainer>
            <OEButton
              loading={isLoading}
              disabled={isLoading}
              onPress={handleSubmit}
            >
              Send
            </OEButton>
          </FooterContainer>
        </>
      )}
    </Container>
  );
};

const Container = styled.View`
  flex-basis: 100%;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom-width: 1px;
  border-bottom-color: ${({ theme }) => theme.color.border};
`;

const TitleContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const FooterContainer = styled.View`
  margin-top: 16px;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;
